import { normalizeControlType } from '../api/normalize/controlType';
import type { CustomField, Frequency, PaymentMethodCode, Service } from '../api/types';
import { computeVisibility, type VisibilityResult } from '../domain/conditionals';
import { headingOverride, paragraphOverride } from '../domain/copyOverrides';
import { sortByOrdering } from '../domain/customFieldValues';
import { stripRequiredMarker } from '../domain/labels';
import { usablePaymentMethods } from '../domain/payments';
import { systemFieldRequirement, type SystemFieldRequirement } from '../domain/validation';
import type { FormState } from './types';

export { SUPPORTED_PAYMENT_METHODS } from '../domain/payments';

export function selectTimeZone(state: FormState): string {
  return state.bootstrap?.settings.timezone || 'UTC';
}

export function selectSelectedService(state: FormState): Service | null {
  const id = state.values.services[0]?.serviceId;
  if (id === undefined) return null;
  return state.bootstrap?.services.find((s) => s.id === id) ?? null;
}

export function selectFrequencies(state: FormState): Frequency[] {
  return state.bootstrap?.frequencies ?? [];
}

export function selectVisibility(state: FormState): VisibilityResult {
  return computeVisibility(
    state.bootstrap?.customFields ?? [],
    state.values.customFields,
    state.rules,
  );
}

/** Visible custom fields in display order, split by placement. */
export function selectCustomFields(state: FormState): {
  main: CustomField[];
  beforeSubmit: CustomField[];
  visibility: VisibilityResult;
} {
  const visibility = selectVisibility(state);
  const fields = sortByOrdering(state.bootstrap?.customFields ?? []).filter(
    (f) => f.code && visibility.visibleCodes.has(f.code),
  );
  return {
    main: fields.filter((f) => f.settings?.before_submit_button !== true),
    beforeSubmit: fields.filter((f) => f.settings?.before_submit_button === true),
    visibility,
  };
}

export function selectRequireZip(state: FormState): boolean {
  return state.bootstrap?.locationPolicy.prevent_booking === true;
}

export function selectSystemField(state: FormState, code: string): SystemFieldRequirement {
  return systemFieldRequirement(state.bootstrap?.form ?? null, code, {
    requireZip: selectRequireZip(state),
  });
}

export function selectHeading(state: FormState, code: string): string | null {
  const heading = state.bootstrap?.form.headings?.find((h) => h.code === code);
  if (!heading || heading.show === false) return null;
  // Some headings are worded by us rather than the tenant (src/domain/copyOverrides.ts);
  // visibility still follows the tenant.
  return headingOverride(code) ?? stripRequiredMarker(heading.label).text ?? null;
}

export function selectParagraph(state: FormState, code: string): string | null {
  const paragraph = state.bootstrap?.form.paragraphs?.[code];
  if (!paragraph || paragraph.show === false) return null;
  // A few paragraphs are worded by us rather than the tenant (src/domain/copyOverrides.ts).
  // Visibility still follows the tenant, so hiding the block in their admin still works.
  return paragraphOverride(code) ?? paragraph.text?.trim() ?? null;
}

export function selectPaymentMethods(state: FormState): PaymentMethodCode[] {
  return usablePaymentMethods(state.bootstrap?.settings);
}

/** Vendor semantics: `recaptcha_disable.default_booking === 1` turns the captcha off. */
export function selectRecaptchaRequired(state: FormState): boolean {
  const disabled = state.bootstrap?.settings.recaptcha_disable?.default_booking;
  return disabled !== 1;
}

export function selectSubmitLabel(state: FormState): string {
  return state.bootstrap?.form.settings?.submit_button?.button_text?.trim() || 'Book Now';
}

export interface CustomFieldsByPlacement {
  mainFields: CustomField[];
  beforeSubmitFields: CustomField[];
  mainIds: number[];
  beforeSubmitIds: number[];
}

/** Placement split regardless of current visibility (for validation and error→pane mapping). */
export function selectCustomFieldPlacement(state: FormState): CustomFieldsByPlacement {
  const all = sortByOrdering(state.bootstrap?.customFields ?? []);
  const mainFields = all.filter((f) => f.settings?.before_submit_button !== true);
  const beforeSubmitFields = all.filter((f) => f.settings?.before_submit_button === true);
  const ids = (fields: CustomField[]) =>
    fields
      .filter((f) => normalizeControlType(f.control_type).type !== 'static')
      .map((f) => f.id)
      .filter((id): id is number => typeof id === 'number');
  return {
    mainFields,
    beforeSubmitFields,
    mainIds: ids(mainFields),
    beforeSubmitIds: ids(beforeSubmitFields),
  };
}

export function selectCustomFieldIds(state: FormState): number[] {
  return sortByOrdering(state.bootstrap?.customFields ?? [])
    .filter((f) => normalizeControlType(f.control_type).type !== 'static')
    .map((f) => f.id)
    .filter((id): id is number => typeof id === 'number');
}

/** Whether an error for `key` should be displayed right now. */
export function shouldShowError(state: FormState, key: string, pane: 1 | 2): boolean {
  return state.attempted[pane] || state.touched[key] === true || state.apiErrors[key] !== undefined;
}
